import { Injectable } from '@angular/core';
import { ApiService } from '../../http/api.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

export interface ProjectDailyByEmployerResponse {
  Assigned: number;
  Active: number;
  Expiry: number;
  Present: number;
  Left: number;
  NewPersonal: number;
  Compliant: number;
  Females: number;
  Males: number;
  CheckedInOnly: number;
  NonCompliant: number;
}

export interface ProjectDailySummaryItem {
  Present: number;
  Active: number;
  CheckedInOnly: number;
  NewPersonal: number;
  Compliant: number;
  NonCompliant: number;
  EmployerId: string;
  EmployerName: string;
}

export interface DynamicDataItem {
  count: number;
  ProjectId: string | null;
  ProjectName: string | null;
  EmployerId: string | null;
  EmployerName: string | null;
}

@Injectable({
  providedIn: 'root'
})

export class DashBoardService {

  constructor(
    private api: ApiService,
    private _http: HttpClient
  ) { }

  // New API method for summary data
  getProjectDailyByEmployer(
    token?: string,
    projectId?: string,
    date?: string,
    category?: string,
    sex?: string,
    employerIds?: string[],
    contractIds?: string[]
  ): Observable<ProjectDailyByEmployerResponse> {
    let url = `api/DynamicData/ProjectDailyByEmployer/${projectId}`;
    const params: string[] = [];

    if (date) {
      params.push(`date=${encodeURIComponent(date)}`);
    }
    if (category && category !== '-1') {
      params.push(`category=${encodeURIComponent(category)}`);
    }
    if (sex && sex !== '-1') {
      params.push(`sex=${encodeURIComponent(sex)}`);
    }
    if (employerIds && employerIds.length > 0) {
      employerIds.forEach(id => {
        if (id && id !== '-1') {
          params.push(`employerId=${encodeURIComponent(id)}`);
        }
      });
    }
    if (contractIds && contractIds.length > 0) {
      contractIds.forEach(id => {
        if (id && id !== '-1') {
          params.push(`contractId=${encodeURIComponent(id)}`);
        }
      });
    }

    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    if (token !== undefined) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      });
      return this._http.get<ProjectDailyByEmployerResponse>(this.api.getUrl(url), { headers });
    } else {
      return this._http.get<ProjectDailyByEmployerResponse>(this.api.getUrl(url));
    }
  }

  // New API method for chart data
  getProjectDailySummary(
    token?: string,
    projectId?: string,
    date?: string,
    category?: string,
    sex?: string,
    employerIds?: string[],
    contractIds?: string[]
  ): Observable<ProjectDailySummaryItem[]> {
    let url = `api/DynamicData/ProjectDailySummary/${projectId}`;
    const params: string[] = [];

    if (date) {
      params.push(`date=${encodeURIComponent(date)}`);
    }
    if (category && category !== '-1') {
      params.push(`category=${encodeURIComponent(category)}`);
    }
    if (sex && sex !== '-1') {
      params.push(`sex=${encodeURIComponent(sex)}`);
    }
    if (employerIds && employerIds.length > 0) {
      employerIds.forEach(id => {
        if (id && id !== '-1') {
          params.push(`employerId=${encodeURIComponent(id)}`);
        }
      });
    }
    if (contractIds && contractIds.length > 0) {
      contractIds.forEach(id => {
        if (id && id !== '-1') {
          params.push(`contractId=${encodeURIComponent(id)}`);
        }
      });
    }

    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    if (token !== undefined) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      });
      return this._http.get<ProjectDailySummaryItem[]>(this.api.getUrl(url), { headers });
    } else {
      return this._http.get<ProjectDailySummaryItem[]>(this.api.getUrl(url));
    }
  }

  // Keep existing method for backward compatibility
  getDynamicProjectDaily(token?, companyId?: string, date?: string): Observable<DynamicDataItem[]> {
    let url = '';

    if (companyId && companyId !== '-1') {
      url = `api/DynamicData/ProjectDaily/${companyId}`;
      if (date) {
        url += `?date=${date}`;
      }
    } else {
      url = 'api/DynamicData/ProjectDaily';
      if (date) {
        url += `?date=${date}`;
      }
    }

    if (token !== undefined) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      });
      return this._http.get<DynamicDataItem[]>(this.api.getUrl(url), { headers: headers });
    } else {
      return this._http.get<DynamicDataItem[]>(this.api.getUrl(url));
    }
  }

  getContractValidateExist(token?): Observable<any>{
    if(token !== undefined) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      });
      return this._http.get(this.api.getUrl(`api/Company/Select`), { headers: headers });
    } else {
      return this._http.get<any>(this.api.getUrl(`api/Company/Select`));
    }
  }

  // Select Filter
  getProjectSelectCompanyInfoId(token?, companyInfoId?): Observable<any> {
    if(token !== undefined) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      });
      return this._http.get<any>(this.api.getUrl(`api/Project/Select${companyInfoId === '-1'? '': '?companyInfoId='+companyInfoId}`), { headers: headers });
    } else {
      return this._http.get<any>(this.api.getUrl(`api/Project/Select${companyInfoId === '-1'? '': '?companyInfoId='+companyInfoId}`));
    }
  }

  getEmployerSelectCompanyInfoId(token?, companyInfoId?, projectId?): Observable<any> {
    let url = ''
    if(companyInfoId === '-1' && projectId === '') {
      url = '';
    } else if(companyInfoId === '-1' && projectId !== '') {
      url = '?projectId='+projectId;
    } else if(companyInfoId !== '-1' && projectId !== '') {
      url = '?companyInfoId='+companyInfoId+'&projectId='+projectId;
    }

    if(token !== undefined) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      });
      return this._http.get<any>(this.api.getUrl(`api/Employer/Select${url}`), { headers: headers });
    } else {
      return this._http.get<any>(this.api.getUrl(`api/Employer/Select${url}`));
    }
  }

  getContractSelectCompanyInfoId(token?, companyInfoId?, employerId?, projectId?): Observable<any> {
    let url = ''
    if(companyInfoId === '-1' && employerId === '' && projectId === '') {
      url = '';
    } else if(companyInfoId === '-1' && employerId !== '' && projectId === '') {
      url = '?employerId='+employerId;
    } else if(companyInfoId !== '-1' && employerId !== '' && projectId === '') {
      url = '?companyInfoId='+companyInfoId+'&employerId='+employerId;
    } else if(companyInfoId !== '-1' && employerId === '' && projectId !== '') {
      url = '?companyInfoId='+companyInfoId+'&projectId='+projectId;
    } else if(companyInfoId !== '-1' && employerId !== '' && projectId !== '') {
      url = '?companyInfoId='+companyInfoId+'&employerId='+employerId+'&projectId='+projectId;
    }

    if(token !== undefined) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      });
      return this._http.get<any>(this.api.getUrl(`api/Contract/Select${url}`), { headers: headers });
    } else {
      return this._http.get<any>(this.api.getUrl(`api/Contract/Select${url}`));
    }
  }

  // Dashboard Config main
  getDashBoardMainCompanyInfoId(token?, companyInfoId?, contractId?, employerId?, projectId?): Observable<any> {
    let url = ''
    if(companyInfoId === '-1' && contractId ==='' && employerId ==='' && projectId === '') {
      url = 'api/DashBoard/Main';
    } if(companyInfoId !== '-1') {
      url = 'api/DashBoard/Main?companyInfoId='+companyInfoId+
        (contractId !== '' ? '&contractId='+contractId : '')+
        (employerId !== '' ? '&employerId='+employerId : '')+
        (projectId !== '' ? '&projectId='+projectId : '');
    } else {
      url = 'api/DashBoard/Main?'+
        (contractId !== '' ? '&contractId='+contractId : '')+
        (employerId !== '' ? '&employerId='+employerId : '')+
        (projectId !== '' ? '&projectId='+projectId : '');
    }

    if(token !== undefined) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      });
      return this._http.get<any>(this.api.getUrl(url), { headers: headers });
    } else {
      return this._http.get<any>(this.api.getUrl(url));
    }
  }

  getDashBoardMainCompanyInfoIdisCount(token?, companyInfoId?, contractId?, employerId?, projectId?): Observable<any> {
    let url = ''
    if(companyInfoId === '-1' && contractId ==='' && employerId ==='' && projectId === '') {
      url = 'api/DashBoard/Main?isCount=true';
    } if(companyInfoId !== '-1') {
      url = 'api/DashBoard/Main?companyInfoId='+companyInfoId+
        (contractId !== '' ? '&contractId='+contractId : '')+
        (employerId !== '' ? '&employerId='+employerId : '')+
        (projectId !== '' ? '&isCount=true&projectId='+projectId : '&isCount=true');
    } else {
      url = 'api/DashBoard/Main?'+
        (contractId !== '' ? '&contractId='+contractId : '')+
        (employerId !== '' ? '&employerId='+employerId : '')+
        (projectId !== '' ? '&projectId='+projectId : '');
    }

    if(token !== undefined) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      });
      return this._http.get<any>(this.api.getUrl(url), { headers: headers });
    } else {
      return this._http.get<any>(this.api.getUrl(url));
    }
  }

  getDashBoardMainCompanyInfoIdbyPersona(token?, companyInfoId?, dashboardPersonalFilterValue?, contractId?, employerId?, projectId?): Observable<any> {
    let url = ''
    if(companyInfoId === '-1' && contractId ==='' && employerId ==='' && projectId === '') {
      url = `api/DashBoard/Main${dashboardPersonalFilterValue}=true`;
    } if(companyInfoId !== '-1') {
      url = 'api/DashBoard/Main?'+dashboardPersonalFilterValue+'=true&companyInfoId='+companyInfoId+
        (contractId !== '' ? '&contractId='+contractId : '')+
        (employerId !== '' ? '&employerId='+employerId : '')+
        (projectId !== '' ? '&projectId='+projectId : '');
    } else {
      url = 'api/DashBoard/Main?'+dashboardPersonalFilterValue+'=true'+
        (contractId !== '' ? '&contractId='+contractId : '')+
        (employerId !== '' ? '&employerId='+employerId : '')+
        (projectId !== '' ? '&projectId='+projectId : '');
    }

    if(token !== undefined) {
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      });
      return this._http.get<any>(this.api.getUrl(url), { headers: headers });
    } else {
      return this._http.get<any>(this.api.getUrl(url));
    }
  }
}
